// hooks/useVariableElements.js - Version CORRIGÉE - Boucle infinie fixée
import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://www.klasys.com';

// =============================================================================
// 🎣 HOOK - DASHBOARD ÉLÉMENTS VARIABLES
// =============================================================================

export const useVariableElementsDashboard = (monthId) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchDashboard = useCallback(async () => {
    if (!monthId) {
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/variable-elements/dashboard/${monthId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setDashboardData(result.data);
      } else {
        throw new Error(result.message || 'Erreur lors de la récupération des données');
      }
    } catch (err) {
      console.error('Erreur dashboard éléments variables:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [monthId]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  return { dashboardData, loading, error, refresh: fetchDashboard };
};

// =============================================================================
// 🎣 HOOK - LISTE DES ÉLÉMENTS VARIABLES (✅ VERSION FIXÉE)
// =============================================================================

export const useVariableElementsList = (monthId) => {
  const [elements, setElements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ✅ NOUVELLE APPROCHE - useEffect direct sans useCallback problématique
  useEffect(() => {
    if (!monthId || monthId === null || monthId === undefined) {
      setLoading(false);
      setElements([]);
      setError(null);
      return;
    }

    let isCancelled = false; // Protection contre les appels multiples

    const fetchElements = async () => {
      if (isCancelled) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/variable-elements/${monthId}/all`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (!isCancelled) {
          if (result.success) {
            setElements(result.data || []);
          } else {
            throw new Error(result.message || 'Erreur lors de la récupération des éléments');
          }
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Erreur liste éléments variables:', err);
          setError(err.message);
          setElements([]);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchElements();

    // ✅ Cleanup function pour annuler les opérations en cours
    return () => {
      isCancelled = true;
    };
  }, [monthId]); // ✅ SEULEMENT monthId dans les dépendances

  // ✅ Fonction refresh séparée pour les rafraîchissements manuels
  const refresh = useCallback(async () => {
    if (!monthId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/variable-elements/${monthId}/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setElements(result.data || []);
      } else {
        throw new Error(result.message || 'Erreur lors de la récupération des éléments');
      }
    } catch (err) {
      console.error('Erreur refresh éléments variables:', err);
      setError(err.message);
      setElements([]);
    } finally {
      setLoading(false);
    }
  }, [monthId]);

  return { elements, loading, error, refresh };
};

// =============================================================================
// 🎣 HOOK - CONFIGURATION
// =============================================================================

export const useVariableElementsConfig = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    
    const fetchConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}/api/variable-elements/config/current`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error(`Erreur ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (isMounted && result.success) {
          setConfig(result.data);
        } else if (isMounted) {
          throw new Error(result.message || 'Erreur lors de la récupération de la configuration');
        }
      } catch (err) {
        if (isMounted) {
          console.error('Erreur configuration éléments variables:', err);
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchConfig();
    
    return () => {
      isMounted = false;
    };
  }, []);

  return { config, loading, error };
};

// =============================================================================
// 🎣 HOOK - CRÉATION D'ÉLÉMENTS
// =============================================================================

export const useCreateVariableElement = () => {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [lastCreated, setLastCreated] = useState(null);

  const createElement = async (elementData) => {
    try {
      setCreating(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/variable-elements`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(elementData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        setLastCreated(result.data);
        return result.data;
      } else {
        throw new Error(result.message || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('Erreur création élément variable:', err);
      setError(err.message);
      throw err;
    } finally {
      setCreating(false);
    }
  };

  const createAbsence = async (absenceData) => {
    const elementData = {
      ...absenceData,
      type: 'absence',
      details_specifiques: {
        type_absence: absenceData.type_absence,
        date_debut: absenceData.date_debut,
        date_fin: absenceData.date_fin,
        nb_jours_calendaires: absenceData.nb_jours_calendaires,
        nb_jours_ouvres: absenceData.nb_jours_ouvres,
        certificat_medical: absenceData.certificat_medical || false,
        impact_salaire: absenceData.impact_salaire !== false,
        taux_deduction: absenceData.taux_deduction || 100.00
      }
    };
    
    return await createElement(elementData);
  };

  const createAvance = async (avanceData) => {
    const elementData = {
      ...avanceData,
      type: 'avance',
      details_specifiques: {
        nb_echeances: avanceData.nb_echeances || 1,
        mois_debut_remboursement: avanceData.mois_debut_remboursement
      }
    };
    
    return await createElement(elementData);
  };

  const createPrime = async (primeData) => {
    const elementData = {
      ...primeData,
      type: 'prime'
    };
    
    return await createElement(elementData);
  };

  const createRetenue = async (retenueData) => {
    const elementData = {
      ...retenueData,
      type: 'retenue'
    };
    
    return await createElement(elementData);
  };

  return {
    createElement,
    createAbsence,
    createAvance,
    createPrime,
    createRetenue,
    creating,
    error,
    lastCreated
  };
};

// =============================================================================
// 🎣 HOOK - VALIDATION D'ÉLÉMENTS (✅ CORRIGÉ)
// =============================================================================

export const useValidateVariableElement = (refreshCallback) => {
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState(null);

  const validateElement = async (elementId, action, notes = null) => {
    try {
      console.log('🔍 validateElement appelée:', elementId, action); // DEBUG
      setValidating(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/variable-elements/${elementId}/validate`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action, notes_validation: notes })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ Réponse validation:', result); // DEBUG
      
      if (result.success) {
        // ✅ CORRECTION: Rafraîchir les données après validation
        if (refreshCallback) {
          console.log('🔄 Rafraîchissement après validation'); // DEBUG
          await refreshCallback();
        }
        return result.data;
      } else {
        throw new Error(result.message || 'Erreur lors de la validation');
      }
    } catch (err) {
      console.error('❌ Erreur validation élément variable:', err);
      setError(err.message);
      throw err;
    } finally {
      setValidating(false);
    }
  };

  const approveElement = async (elementId, notes = null) => {
    return await validateElement(elementId, 'approve', notes);
  };

  const rejectElement = async (elementId, notes = null) => {
    return await validateElement(elementId, 'reject', notes);
  };

  return {
    validateElement,
    approveElement,
    rejectElement,
    validating,
    error
  };
};

// =============================================================================
// 🎣 HOOK MANAGER - VERSION CORRIGÉE
// =============================================================================

export const useVariableElementsManager = (monthId) => {
  const dashboard = useVariableElementsDashboard(monthId);
  const elementsList = useVariableElementsList(monthId);
  const config = useVariableElementsConfig();
  
  // ✅ CORRECTION: Fonction refresh globale
  const refreshAll = useCallback(async () => {
    console.log('🔄 refreshAll appelée'); // DEBUG
    await Promise.all([
      dashboard.refresh(),
      elementsList.refresh()
    ]);
  }, [dashboard.refresh, elementsList.refresh]);
  
  const creator = useCreateVariableElement();
  const validator = useValidateVariableElement(refreshAll); // ✅ Passer le callback

  return {
    // Dashboard
    dashboardData: dashboard.dashboardData,
    dashboardLoading: dashboard.loading,
    dashboardError: dashboard.error,
    
    // Liste des éléments
    elements: elementsList.elements,
    elementsLoading: elementsList.loading,
    elementsError: elementsList.error,
    
    // Création
    createElement: creator.createElement,
    createAbsence: creator.createAbsence,
    createAvance: creator.createAvance,
    createPrime: creator.createPrime,
    createRetenue: creator.createRetenue,
    creating: creator.creating,
    createError: creator.error,
    lastCreated: creator.lastCreated,
    
    // Validation
    validateElement: validator.validateElement,
    approveElement: validator.approveElement,
    rejectElement: validator.rejectElement,
    validating: validator.validating,
    validateError: validator.error,
    
    // Configuration
    config: config.config,
    configLoading: config.loading,
    configError: config.error,
    
    // ✅ NOUVEAU: Fonction refresh globale
    refreshAll
  };
};